import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { AlertTriangle, ChevronDown, Info } from "lucide-react";
import { typographyTokens, spacingTokens, borderRadiusTokens } from "../../config/designTokens";
import { useAuth } from "../../contexts/AuthContext";
import {
  currentDemoPersona,
  LISTED_DEMO_PERSONAS,
  demoSignupUrl,
  isDemo,
} from "../../config/demo";
import { capture } from "../../analytics/posthog";
import { EVENTS } from "../../analytics/events";

/**
 * SystemBanner — a full-width notice pinned to the very top of the viewport,
 * above all app chrome. Reserved for SEVERE / app-wide situations (outage,
 * maintenance, security advisory). It is OFF by default so it never distracts;
 * set `SYSTEM_BANNER` below to surface one.
 *
 * The one standing exception is the Live sample (PRD 117, greenbackapp/docs#151):
 * while a visitor is signed in to a sample business, the bar says so, offers
 * the other samples and links to signing up for real.
 *
 * (Beta status is communicated subtly via the "Beta" badge on the logo, not a
 * persistent bar.)
 */

export type SystemBannerSeverity = "critical" | "warning" | "info";

export interface SystemBannerConfig {
  message: string;
  severity?: SystemBannerSeverity;
}

/** The single source of truth for the current top banner. `null` = nothing shown. */
export const SYSTEM_BANNER = null as SystemBannerConfig | null;

const BANNER_HEIGHT_PX = 36;
const DEMO_BANNER_HEIGHT_PX = 44;
const HEIGHT_VAR = "--system-banner-height";

export const DEMO_BANNER_MESSAGE = "Kicking the tires? Nothing you change is saved.";

/**
 * App-shell top offset. A CSS variable rather than a constant because the
 * demo bar appears only once a sample session is signed in, after the styled
 * components that read this were built. SystemBanner sets the variable; with
 * no banner it is unset and resolves to "0px", so the app has zero top offset.
 */
export const SYSTEM_BANNER_HEIGHT = `var(${HEIGHT_VAR}, 0px)`;

const setBannerHeight = (px: number) => {
  try {
    if (px > 0) document.documentElement.style.setProperty(HEIGHT_VAR, `${px}px`);
    else document.documentElement.style.removeProperty(HEIGHT_VAR);
  } catch {
    // No DOM: nothing to offset.
  }
};

const onDemoPublicPage = (): boolean => {
  try {
    const { pathname } = window.location;
    return pathname === "/demo" || pathname.startsWith("/demo/");
  } catch {
    return false;
  }
};

export const SystemBanner: React.FC = () => {
  const { user } = useAuth();
  // Not on the sample's own public pages (/demo, /demo/<persona>, /demo/ended),
  // which are reached by a full page load and say what they are themselves.
  const demo = isDemo() && Boolean(user?.authToken) && !onDemoPublicPage();
  const height = demo ? DEMO_BANNER_HEIGHT_PX : SYSTEM_BANNER ? BANNER_HEIGHT_PX : 0;

  useLayoutEffect(() => {
    setBannerHeight(height);
  }, [height]);
  useEffect(() => () => setBannerHeight(0), []);

  if (demo) return <DemoBanner />;
  if (!SYSTEM_BANNER) return null;
  const severity = SYSTEM_BANNER.severity ?? "info";
  const Icon = severity === "info" ? Info : AlertTriangle;
  return (
    <Bar role="alert" $severity={severity} $height={BANNER_HEIGHT_PX}>
      <Icon size={15} />
      <Message>{SYSTEM_BANNER.message}</Message>
    </Bar>
  );
};

/**
 * The Live sample's bar. Links are plain anchors, not router links: the
 * banner sits above the router, and a full page load is exactly right for
 * switching samples (the start page signs in afresh) or leaving for the real
 * sign-up, which is on the real origin so the demo token never travels.
 */
const DemoBanner: React.FC = () => {
  const persona = currentDemoPersona();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [menuOpen]);

  return (
    <Bar role="region" aria-label="Sample business" $severity="info" $height={DEMO_BANNER_HEIGHT_PX} data-testid="demo-banner">
      <Info size={15} />
      <Message $wide>{DEMO_BANNER_MESSAGE}</Message>
      <Message $narrow aria-hidden>Sample business</Message>
      <MenuWrap ref={menuRef}>
        <BarButton
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(open => !open)}
        >
          See another sample <ChevronDown size={14} />
        </BarButton>
        {menuOpen && (
          <Menu role="menu">
            {LISTED_DEMO_PERSONAS.filter(option => option.key !== persona?.key).map(option => (
              <MenuLink
                key={option.key}
                role="menuitem"
                href={`/demo/${option.key}`}
                onClick={() => capture(EVENTS.DEMO_CTA_CLICKED, { cta: "see_another_sample", to: option.key })}
              >
                {option.label}
              </MenuLink>
            ))}
          </Menu>
        )}
      </MenuWrap>
      <CtaLink
        href={demoSignupUrl(persona?.key)}
        onClick={() => capture(EVENTS.DEMO_CTA_CLICKED, { cta: "start_your_books", surface: "banner" })}
      >
        Start your books
      </CtaLink>
    </Bar>
  );
};

const Bar = styled.div<{ $severity: SystemBannerSeverity; $height: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${props => props.$height}px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacingTokens.sm};
  padding: 0 ${spacingTokens.lg};
  background-color: ${props =>
    props.$severity === "critical"
      ? (props.theme.semantic?.negative ?? "#F0483E")
      : props.$severity === "warning"
        ? (props.theme.semantic?.warning ?? "#F59E0B")
        : (props.theme.brand?.primary ?? "#16C784")};
  color: ${props => props.theme.text.onBrand};
  z-index: 1200;

  @media (max-width: 640px) {
    padding: 0 ${spacingTokens.sm};
    gap: ${spacingTokens.xs};
    & > svg {
      display: none;
    }
  }
`;

const Message = styled.span<{ $wide?: boolean; $narrow?: boolean }>`
  color: ${props => props.theme.text.onBrand};
  font-family: ${typographyTokens.fontFamily.medium};
  font-size: ${typographyTokens.fontSize.caption};
  font-weight: ${typographyTokens.fontWeight.medium};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  display: ${props => (props.$narrow ? "none" : "inline")};

  /* A phone cannot fit the sentence beside two controls: it gets two words. */
  @media (max-width: 600px) {
    display: ${props => (props.$wide ? "none" : "inline")};
  }

  /* The narrowest phones keep the two controls and drop the words. */
  @media (max-width: 420px) {
    display: none;
  }
`;

const MenuWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const BarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 1px solid ${props => props.theme.text.onBrand};
  border-radius: ${borderRadiusTokens.md};
  color: ${props => props.theme.text.onBrand};
  font-family: ${typographyTokens.fontFamily.medium};
  font-size: ${typographyTokens.fontSize.caption};
  padding: 4px 10px;
  cursor: pointer;
  white-space: nowrap;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  padding: 4px;
  background: ${props => props.theme.background.paper};
  border: 1px solid ${props => props.theme.border.default};
  border-radius: ${borderRadiusTokens.md};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
`;

const MenuLink = styled.a`
  padding: 8px 12px;
  border-radius: ${borderRadiusTokens.sm};
  color: ${props => props.theme.text.primary};
  font-family: ${typographyTokens.fontFamily.medium};
  font-size: ${typographyTokens.fontSize.caption};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    background: ${props => props.theme.background.subtle};
  }
`;

const CtaLink = styled.a`
  flex-shrink: 0;
  background: ${props => props.theme.text.onBrand};
  color: ${props => props.theme.brand?.primary ?? "#16C784"};
  border-radius: ${borderRadiusTokens.md};
  font-family: ${typographyTokens.fontFamily.semiBold};
  font-size: ${typographyTokens.fontSize.caption};
  font-weight: ${typographyTokens.fontWeight.semibold};
  padding: 5px 12px;
  text-decoration: none;
  white-space: nowrap;
`;

export default SystemBanner;
